import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { API_BASE, apiFetch } from "../../utils/api";
import "./AdminProducts.css";

/* webpack require.context for product images */
let PRODUCT_IMAGES = {};
try {
  const ctx = require.context(
    "../../assets/uploads/products",
    false,
    /\.(png|jpe?g|gif|webp|svg)$/i
  );
  PRODUCT_IMAGES = ctx.keys().reduce((map, key) => {
    map[key.replace(/^\.\//, "")] = ctx(key);
    return map;
  }, {});
} catch (_) {
  /* running outside CRA — images will fall back to API URL */
}

function productImg(filename) {
  if (!filename) return null;
  return PRODUCT_IMAGES[filename] ?? `${API_BASE}/uploads/products/${filename}`;
}

/** Format price as ZAR */
function formatPrice(val) {
  if (val === null || val === undefined || val === "") return "—";
  return `R ${parseFloat(val).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Derive stock badge variant */
function stockVariant(stock) {
  const n = parseInt(stock) || 0;
  if (n <= 0)  return "apd-stock--out";
  if (n <= 5)  return "apd-stock--low";
  return "apd-stock--in";
}

function stockLabel(stock) {
  const n = parseInt(stock) || 0;
  if (n <= 0) return "Out of stock";
  if (n <= 5) return `Low — ${n}`;
  return `${n} in stock`;
}

const EMPTY_FORM = {
  title:       "",
  slug:        "",
  description: "",
  price:       "",
  category:    "",
  stock:       "0",
  order:       "0",
  active:      1,
  image:       null, // new file upload
};

/* ─────────────────────────────────
   Dot-menu
   ───────────────────────────────── */
function DotMenu({ onView, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="apd-dot-menu" ref={ref}>
      <button
        className="apd-dot-menu__trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="Actions"
      >
        <span /><span /><span />
      </button>
      {open && (
        <div className="apd-dot-menu__dropdown">
          <button onClick={() => { onView();   setOpen(false); }}>
            <span className="apd-dot-menu__icon">👁</span> View
          </button>
          <button onClick={() => { onEdit();   setOpen(false); }}>
            <span className="apd-dot-menu__icon">✏</span> Edit
          </button>
          <button
            className="apd-dot-menu__delete"
            onClick={() => { onDelete(); setOpen(false); }}
          >
            <span className="apd-dot-menu__icon">🗑</span> Delete
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────
   Status badge
   ───────────────────────────────── */
function StatusBadge({ active }) {
  const isActive = Number(active) === 1;
  return (
    <span className={`apd-badge ${isActive ? "apd-badge--active" : "apd-badge--inactive"}`}>
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

/* ═══════════════════════════════════
   VIEW MODAL
   ═══════════════════════════════════ */
function ViewModal({ product, onClose }) {
  if (!product) return null;

  return (
    <div className="apd-modal-overlay" onClick={onClose}>
      <div className="apd-modal apd-modal--view" onClick={(e) => e.stopPropagation()}>
        <div className="apd-modal__header">
          <div className="apd-modal__title-row">
            <span className="apd-modal__tag">VIEW</span>
            <h2 className="apd-modal__title">{product.title || "Product"}</h2>
          </div>
          <button className="apd-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="apd-modal__body apd-modal__body--view">
          {/* Image */}
          {product.image_filename ? (
            <img
              src={productImg(product.image_filename)}
              alt={product.title || ""}
              className="apd-view-image"
            />
          ) : (
            <div style={{
              background: "#f3f4f6", height: 160, display: "flex",
              alignItems: "center", justifyContent: "center",
              borderRadius: 2, color: "#ccc", fontSize: "2rem",
            }}>
              📦
            </div>
          )}

          {/* Details grid */}
          <div className="apd-view-grid">
            {[
              ["Title",    product.title    || "—"],
              ["Slug",     product.slug     || "—"],
              ["Price",    formatPrice(product.price)],
              ["Category", product.category || "—"],
              ["Stock",    stockLabel(product.stock)],
              ["Order",    product.order    ?? "—"],
              ["Status",   Number(product.active) === 1 ? "Active" : "Inactive"],
            ].map(([label, val]) => (
              <div key={label} className="apd-view-row">
                <span className="apd-view-row__label">{label}</span>
                <span className="apd-view-row__val">{val}</span>
              </div>
            ))}
          </div>

          {/* Description — plain text */}
          {product.description && (
            <div className="apd-view-description">
              <h4>Description</h4>
              <p className="apd-view-description__text">{product.description}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   CREATE / EDIT MODAL
   ═══════════════════════════════════ */
function FormModal({ mode, product, onClose, onSaved }) {
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [preview, setPreview] = useState(null);
  const [clearImg, setClearImg] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");

  const slugify = (str) =>
    str.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "").replace(/-+/g, "-").replace(/^-|-$/g, "");

  /* Populate when editing */
  useEffect(() => {
    if (mode === "edit" && product) {
      setForm({
        title:       product.title       || "",
        slug:        product.slug        || "",
        description: product.description || "",
        price:       product.price       ?? "",
        category:    product.category    || "",
        stock:       product.stock       ?? "0",
        order:       product.order       ?? "0",
        active:      Number(product.active) === 1 ? 1 : 0,
        image:       null,
      });
      setPreview(product.image_filename ? productImg(product.image_filename) : null);
      setClearImg(false);
    }
  }, [mode, product]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleTitleChange = (val) => {
    setForm((f) => ({
      ...f,
      title: val,
      slug:  mode === "create" ? slugify(val) : f.slug,
    }));
  };

  const handleFile = (file) => {
    if (!file) return;
    set("image", file);
    setClearImg(false);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleClear = () => {
    set("image", null);
    setPreview(null);
    setClearImg(true);
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.title.trim()) return setError("Product title is required.");
    if (!form.price || isNaN(parseFloat(form.price))) return setError("A valid price is required.");

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title",       form.title.trim());
      fd.append("slug",        form.slug.trim() || slugify(form.title.trim()));
      fd.append("description", form.description || "");
      fd.append("price",       parseFloat(form.price) || 0);
      fd.append("category",    form.category || "");
      fd.append("stock",       parseInt(form.stock) || 0);
      fd.append("order",       parseInt(form.order)  || 0);
      fd.append("active",      form.active);
      if (form.image) fd.append("image_filename", form.image);
      if (clearImg)   fd.append("clear_image", "1");

      if (mode === "create") {
        await apiFetch("/api/admin/products", { method: "POST", body: fd });
      } else {
        await apiFetch(`/api/admin/products/${product.id}`, { method: "PUT", body: fd });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="apd-modal-overlay" onClick={onClose}>
      <div className="apd-modal apd-modal--form" onClick={(e) => e.stopPropagation()}>
        <div className="apd-modal__header">
          <div className="apd-modal__title-row">
            <span className="apd-modal__tag">{mode === "create" ? "NEW" : "EDIT"}</span>
            <h2 className="apd-modal__title">
              {mode === "create" ? "Add Product" : "Edit Product"}
            </h2>
          </div>
          <button className="apd-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="apd-modal__body apd-modal__body--form">
          {error && <div className="apd-modal__error">⚠ {error}</div>}

          {/* ── Section: Product Details ── */}
          <div className="apd-form-section">
            <h3 className="apd-form-section__title">Product Details</h3>
            <div className="apd-form-grid">
              <div className="apd-form-field apd-form-field--full">
                <label>Title <span className="req">*</span></label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="e.g. Roof Insulation Panel"
                />
              </div>

              <div className="apd-form-field apd-form-field--full">
                <label>Slug</label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => set("slug", e.target.value)}
                  placeholder="e.g. roof-insulation-panel"
                />
                <p className="apd-form-hint">
                  Auto-generated from title. Used in the URL: <code>/products/roof-insulation-panel</code>
                </p>
              </div>

              <div className="apd-form-field apd-form-field--full">
                <label>Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="e.g. Proper insulation and ventilation are key — also offers Ice Guard services to prevent ice buildup and damage."
                />
              </div>
            </div>
          </div>

          {/* ── Section: Pricing & Inventory ── */}
          <div className="apd-form-section">
            <h3 className="apd-form-section__title">Pricing &amp; Inventory</h3>
            <div className="apd-form-grid">
              <div className="apd-form-field">
                <label>Price <span className="req">*</span></label>
                <div className="apd-input-prefix-wrap">
                  <span className="apd-input-prefix">R</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.price}
                    onChange={(e) => set("price", e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="apd-form-field">
                <label>Category</label>
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => set("category", e.target.value)}
                  placeholder="e.g. Insulation"
                />
              </div>

              <div className="apd-form-field">
                <label>Stock</label>
                <input
                  type="number"
                  min="0"
                  value={form.stock}
                  onChange={(e) => set("stock", e.target.value)}
                  placeholder="0"
                />
              </div>

              <div className="apd-form-field">
                <label>Display Order</label>
                <input
                  type="number"
                  min="0"
                  value={form.order}
                  onChange={(e) => set("order", e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* ── Section: Image ── */}
          <div className="apd-form-section">
            <h3 className="apd-form-section__title">Product Image</h3>
            <label className="apd-image-upload">
              {preview ? (
                <img src={preview} alt="" className="apd-image-upload__preview" />
              ) : (
                <div className="apd-image-upload__placeholder">
                  <span>📦</span>
                  <span>Click to upload image</span>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => handleFile(e.target.files[0])}
              />
              <span className="apd-image-upload__label">
                {preview ? "Click to change image" : "Upload Image"}
              </span>
            </label>
            {preview && (
              <button
                type="button"
                className="apd-image-clear"
                style={{ marginTop: 6 }}
                onClick={handleClear}
              >
                Remove image
              </button>
            )}
            <p className="apd-form-hint">
              Images are saved to <code>src/assets/uploads/products/</code>. Leave blank to keep existing.
            </p>
          </div>

          {/* ── Section: Options ── */}
          <div className="apd-form-section">
            <h3 className="apd-form-section__title">Options</h3>
            <div className="apd-form-grid apd-form-grid--checks">
              <label className="apd-check">
                <input
                  type="checkbox"
                  checked={Number(form.active) === 1}
                  onChange={(e) => set("active", e.target.checked ? 1 : 0)}
                />
                <span>Active (visible on site)</span>
              </label>
            </div>
          </div>
        </div>

        <div className="apd-modal__footer">
          <button className="apd-btn apd-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="apd-btn apd-btn--save" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : mode === "create" ? "Create Product" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   DELETE MODAL
   ═══════════════════════════════════ */
function DeleteModal({ product, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState("");

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await apiFetch(`/api/admin/products/${product.id}`, { method: "DELETE" });
      onDeleted();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="apd-modal-overlay" onClick={onClose}>
      <div className="apd-modal apd-modal--delete" onClick={(e) => e.stopPropagation()}>
        <div className="apd-delete__icon">🗑</div>
        <h2 className="apd-delete__title">Delete Product?</h2>
        <p className="apd-delete__body">
          You are about to permanently delete{" "}
          <strong>{product?.title || "this product"}</strong>. This will also remove the
          associated image file. This action cannot be undone.
        </p>
        {error && <div className="apd-modal__error">⚠ {error}</div>}
        <div className="apd-delete__actions">
          <button className="apd-btn apd-btn--cancel" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button className="apd-btn apd-btn--delete" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminProducts() {
  const [products,   setProducts]   = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");

  /* Filters */
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [stockFilter,  setStockFilter]  = useState("");
  const [sort,         setSort]         = useState("order_asc");
  const [page,         setPage]         = useState(1);
  const searchTimer = useRef(null);

  /* Modals */
  const [modal,  setModal]  = useState(null);
  const [target, setTarget] = useState(null);

  /* ── Load products ── */
  const loadProducts = useCallback(() => {
    setLoading(true);
    setError("");
    const q = new URLSearchParams();
    if (search)       q.set("search", search);
    if (statusFilter) q.set("active", statusFilter);
    if (stockFilter)  q.set("stock",  stockFilter);
    q.set("sort",  sort);
    q.set("page",  page);
    q.set("limit", "15");

    apiFetch(`/api/admin/products?${q}`)
      .then((data) => {
        setProducts(data.products    || []);
        setPagination(data.pagination || { page: 1, total: 0, pages: 1 });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, statusFilter, stockFilter, sort, page]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  /* ── Handlers ── */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setPage(1), 400);
  };

  const openModal  = (type, prod = null) => { setModal(type); setTarget(prod); };
  const closeModal = () => { setModal(null); setTarget(null); };
  const afterSave  = () => { closeModal(); loadProducts(); };

  return (
    <AdminLayout pageTag="CONTENT" pageTitle="Products">

      {error && (
        <div className="apd-error-bar">⚠ {error}</div>
      )}

      {/* ── Toolbar ── */}
      <div className="apd-toolbar">
        <div className="apd-toolbar__left">
          <div className="apd-search-wrap">
            <span className="apd-search-wrap__icon">🔍</span>
            <input
              className="apd-search"
              type="text"
              placeholder="Search by title, slug or category…"
              value={search}
              onChange={handleSearchChange}
            />
            {search && (
              <button
                className="apd-search-wrap__clear"
                onClick={() => { setSearch(""); setPage(1); }}
              >✕</button>
            )}
          </div>

          <select
            className="apd-filter-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>

          <select
            className="apd-filter-select"
            value={stockFilter}
            onChange={(e) => { setStockFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Stock</option>
            <option value="in">In Stock</option>
            <option value="low">Low Stock (≤5)</option>
            <option value="out">Out of Stock</option>
          </select>

          <select
            className="apd-filter-select"
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
          >
            <option value="order_asc">Order ↑</option>
            <option value="order_desc">Order ↓</option>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="title_asc">Title A–Z</option>
            <option value="price_asc">Price ↑</option>
            <option value="price_desc">Price ↓</option>
            <option value="stock_asc">Stock ↑</option>
          </select>
        </div>

        <div className="apd-toolbar__right">
          <span className="apd-toolbar__count">
            {loading
              ? "…"
              : `${pagination.total} product${pagination.total !== 1 ? "s" : ""}`}
          </span>
          <button className="apd-btn-primary" onClick={() => openModal("create")}>
            + Add Product
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="apd-table-skeleton" />
      ) : products.length === 0 ? (
        <div className="apd-empty">
          <span>📦</span>
          <p>No products found.</p>
          {(search || statusFilter || stockFilter) && (
            <button
              className="apd-btn apd-btn--cancel"
              onClick={() => { setSearch(""); setStatusFilter(""); setStockFilter(""); setPage(1); }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="apd-table-wrap">
          <table className="apd-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Title / Slug</th>
                <th>Description</th>
                <th>Price</th>
                <th>Category</th>
                <th>Stock</th>
                <th>Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((prod) => (
                <tr key={prod.id}>
                  <td>
                    {prod.image_filename ? (
                      <img
                        src={productImg(prod.image_filename)}
                        alt=""
                        className="apd-img-thumb"
                      />
                    ) : (
                      <span className="apd-no-img">—</span>
                    )}
                  </td>
                  <td>
                    <div className="apd-title-cell">
                      <span className="apd-title-cell__name">{prod.title || "—"}</span>
                      {prod.slug && (
                        <span className="apd-title-cell__slug">/{prod.slug}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="apd-desc-preview">
                      {prod.description
                        ? prod.description.slice(0, 100) + (prod.description.length > 100 ? "…" : "")
                        : "—"}
                    </span>
                  </td>
                  <td>
                    <span className="apd-price">{formatPrice(prod.price)}</span>
                  </td>
                  <td>
                    {prod.category
                      ? <span className="apd-category-badge">{prod.category}</span>
                      : <span className="apd-no-img">—</span>}
                  </td>
                  <td>
                    <span className={`apd-stock ${stockVariant(prod.stock)}`}>
                      {stockLabel(prod.stock)}
                    </span>
                  </td>
                  <td>
                    <span className="apd-order-badge">{prod.order ?? 0}</span>
                  </td>
                  <td>
                    <StatusBadge active={prod.active} />
                  </td>
                  <td>
                    <DotMenu
                      onView={()   => openModal("view",   prod)}
                      onEdit={()   => openModal("edit",   prod)}
                      onDelete={() => openModal("delete", prod)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {pagination.pages > 1 && (
        <div className="apd-pagination">
          <button
            className="apd-pagination__btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >← Prev</button>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`apd-pagination__btn ${p === page ? "active" : ""}`}
              onClick={() => setPage(p)}
            >{p}</button>
          ))}
          <button
            className="apd-pagination__btn"
            disabled={page >= pagination.pages}
            onClick={() => setPage((p) => p + 1)}
          >Next →</button>
        </div>
      )}

      {/* ── Modals ── */}
      {modal === "view"   && <ViewModal   product={target} onClose={closeModal} />}
      {modal === "create" && <FormModal   mode="create"    onClose={closeModal} onSaved={afterSave} />}
      {modal === "edit"   && <FormModal   mode="edit"      product={target} onClose={closeModal} onSaved={afterSave} />}
      {modal === "delete" && <DeleteModal product={target} onClose={closeModal} onDeleted={afterSave} />}

    </AdminLayout>
  );
}